import type { SelectChangeEvent } from '@mui/material';
import type { AxiosError } from 'axios';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEnvConfig } from '@configs/env';
import styled from '@emotion/styled';
import { Box, Button, Typography, useTheme, Select, MenuItem, CircularProgress } from '@mui/material';
import { PageTemplate } from '@components/template';
import { useStore } from '@stores/RootStore';
import sessionStorage from '@repositories/browserStorage/sessionStorage/SessionStorage';
import type { SessionStorageKey } from '@repositories/browserStorage/sessionStorage/SessionStorageKey';
import type { TenantSsoProviderPublicDTO } from '@repositories/tenantSsoProviderRepository/tenantSsoProvider.types';
import { useSsoRedirect, getSsoProviderIcon } from '@customHooks/useSsoRedirect';
import { useGetMyTenantList } from '@queryHooks/useTenant';
import { useDiscoverLoginMethod } from '@queryHooks/useTenantSsoProvider';
import { useExchangeToken } from '@queryHooks/useUser';
import { withLogin } from '@hoc/withLogin';

const SSO_LOGIN_EMAIL_KEY: SessionStorageKey = 'sso_login_email';

type SelectTenantStep = 'TENANT_SELECT' | 'SSO_PROVIDER_SELECT';

type Props = {
  isItAdminPage?: boolean;
};

export const SelectTenant = withLogin(({ isItAdminPage }: Props) => {
  const { t } = useTranslation();
  const {
    authStore,
    uiStore: { alertStore },
  } = useStore();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { PASS_TENANT_SELECT_PAGE } = getEnvConfig();

  // location.state에서 email 수신, 없으면 SSO 리다이렉트 전 저장한 값 복원
  // || 사용: 빈 문자열도 falsy로 처리하여 sessionStorage fallback 동작 보장
  const rawSsoEmail = sessionStorage.get(SSO_LOGIN_EMAIL_KEY);
  const stateEmail =
    location.state && typeof location.state === 'object' && 'email' in location.state && typeof location.state.email === 'string'
      ? location.state.email
      : undefined;
  const userEmail = stateEmail || (typeof rawSsoEmail === 'string' ? rawSsoEmail : undefined) || '';

  // 단계 상태
  const [step, setStep] = useState<SelectTenantStep>('TENANT_SELECT');
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [selectedSsoProvider, setSelectedSsoProvider] = useState<string | null>(null);
  const [ssoProviders, setSsoProviders] = useState<TenantSsoProviderPublicDTO[]>([]);

  // API 훅
  const { data: myTenants } = useGetMyTenantList({});
  const { mutate: discoverLoginMethod, isPending: isDiscoverPending } = useDiscoverLoginMethod();
  const { mutate: exchangeToken, isPending: isExchangePending } = useExchangeToken();
  const { redirectToSsoProvider, isRedirecting } = useSsoRedirect();

  useEffect(() => {
    if (myTenants?.length) {
      setSelectedTenant(myTenants[0].id);
    }
  }, [myTenants]);

  const handleChangeTenant = (e: SelectChangeEvent<string>) => {
    setSelectedTenant(e.target.value);

    // 테넌트 변경 시 SSO 선택 단계 초기화
    if (step === 'SSO_PROVIDER_SELECT') {
      setStep('TENANT_SELECT');
      setSsoProviders([]);
      setSelectedSsoProvider(null);
    }
  };

  const handleChangeSsoProvider = (e: SelectChangeEvent<string>) => {
    setSelectedSsoProvider(e.target.value);
  };

  const showTokenExchangeError = () => {
    alertStore.open({
      title: t('Common.error'),
      message: t('SelectTenant.tokenExchangeError'),
      useCancel: false,
    });
  };

  // SSO 프로바이더 목록으로 리다이렉트 또는 선택 화면 전환
  const handleSsoProviders = (providers: TenantSsoProviderPublicDTO[]) => {
    if (providers.length === 1) {
      redirectToSsoProvider(providers[0], userEmail);
    } else if (providers.length > 1) {
      setSsoProviders(providers);
      setSelectedSsoProvider(providers[0].registrationId);
      setStep('SSO_PROVIDER_SELECT');
    } else {
      showTokenExchangeError();
    }
  };

  // 풀 토큰으로 교환 후 스토어 이동
  // skipSsoFallback: 이미 discover를 수행한 경우 SSO 에러 시 재조회 방지
  const proceedWithTokenExchange = (tenantId: string, skipSsoFallback = false) => {
    exchangeToken(
      { grantType: 'token_exchange', tenantId },
      {
        onSuccess: user => {
          const success = authStore.upgradeToFullToken(user.additionalPermissions, tenantId);
          if (!success) {
            navigate('/error', {
              state: { errorNum: 403, errorText: 'INVALID_TENANT' },
            });
            return;
          }

          sessionStorage.removeItem(SSO_LOGIN_EMAIL_KEY);
          if (PASS_TENANT_SELECT_PAGE === 'true') {
            window.location.href = `/manager/${tenantId}/store`;
          } else {
            navigate('/', { replace: true });
          }
        },
        onError: (error: AxiosError) => {
          if (skipSsoFallback) {
            showTokenExchangeError();
            return;
          }

          const responseData = error.response?.data;
          const hasErrorCode =
            responseData && typeof responseData === 'object' && 'errorCode' in responseData;
          const errorCode = hasErrorCode ? (responseData).errorCode : undefined;
          const errorMessageId =
            errorCode && typeof errorCode === 'object' && 'messageId' in errorCode && typeof errorCode.messageId === 'string'
              ? errorCode.messageId
              : undefined;

          // SSO 필수 테넌트: 토큰 교환 불가 → discover로 SSO 프로바이더 조회 후 리다이렉트
          if (errorMessageId === 'AUTH.ERROR.TENANT_SSO_AUTH_REQUIRED') {
            discoverLoginMethod(
              { email: userEmail, tenantId },
              {
                onSuccess: result => handleSsoProviders(result.ssoProviders ?? []),
                onError: () => showTokenExchangeError(),
              },
            );
            return;
          }

          showTokenExchangeError();
        },
      },
    );
  };

  // 테넌트 선택 후 "다음" 클릭
  const handleTenantNext = () => {
    if (!selectedTenant) return;

    // 제한 토큰 상태가 아닌 경우 (풀 토큰으로 접근한 기존 플로우)
    if (!authStore.isLimitedToken) {
      try {
        authStore.updateCurTenant(selectedTenant);
        authStore.postLogin();
      } catch {
        showTokenExchangeError();
        return;
      }

      sessionStorage.removeItem(SSO_LOGIN_EMAIL_KEY);
      if (PASS_TENANT_SELECT_PAGE === 'true') {
        window.location.href = `/manager/${selectedTenant}/store`;
      } else {
        navigate('/', { replace: true });
      }
      return;
    }

    // 제한 토큰: auth/discover로 해당 테넌트의 로그인 방식 확인
    discoverLoginMethod(
      { email: userEmail, tenantId: selectedTenant },
      {
        onSuccess: result => {
          if (result.loginMethod === 'TENANT_SSO') {
            const providers = result.ssoProviders ?? [];
            if (providers.length === 0) {
              // SSO 프로바이더 정보 없음 → token exchange로 진행 (discover 재조회 방지)
              proceedWithTokenExchange(selectedTenant, true);
              return;
            }
            handleSsoProviders(providers);
          } else {
            // NORMAL 테넌트 → token exchange로 풀 토큰 획득 (discover 재조회 방지)
            proceedWithTokenExchange(selectedTenant, true);
          }
        },
        onError: () => {
          // discover 실패 → token exchange 시도
          proceedWithTokenExchange(selectedTenant);
        },
      },
    );
  };

  // SSO 프로바이더 선택 후 "다음" 클릭
  const handleSsoProviderNext = () => {
    if (!selectedSsoProvider) return;

    const provider = ssoProviders.find(p => p.registrationId === selectedSsoProvider);
    if (provider) {
      redirectToSsoProvider(provider, userEmail);
    }
  };

  const isLoading = isDiscoverPending || isExchangePending || isRedirecting;
  const showSsoProviderSection = step === 'SSO_PROVIDER_SELECT';

  const isNextDisabled = step === 'TENANT_SELECT' ? !selectedTenant || isLoading : !selectedSsoProvider || isLoading;

  const handleNextClick = step === 'TENANT_SELECT' ? handleTenantNext : handleSsoProviderNext;

  return (
    <PageTemplate>
      <Container $isItAdminPage={isItAdminPage}>
        <FormWrapper>
          <TitleWrapper>
            <Typography variant="headline3">{t('SelectTenant.title')}</Typography>
          </TitleWrapper>

          {/* 테넌트 선택 */}
          <SelectWrapper>
            {myTenants && (
              <Select
                size="large"
                fullWidth
                defaultValue={myTenants[0]?.id}
                onChange={handleChangeTenant}
                disabled={isLoading}
              >
                {myTenants.map(tenant => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </SelectWrapper>

          {/* SSO 프로바이더 선택 (슬라이드 애니메이션) */}
          <SsoProviderSection $visible={showSsoProviderSection}>
            <SsoProviderContent>
              <Typography variant="body2" color={theme.colors['text-primary']}>
                {t('SelectTenant.ssoProviderLabel')}
              </Typography>
              {ssoProviders.length > 0 && (
                <Select
                  size="large"
                  fullWidth
                  value={selectedSsoProvider ?? ''}
                  onChange={handleChangeSsoProvider}
                  disabled={isRedirecting}
                >
                  {ssoProviders.map(provider => (
                    <MenuItem key={provider.registrationId} value={provider.registrationId}>
                      <SsoProviderMenuItem>
                        {getSsoProviderIcon(provider.issuerUri)}
                        <span>{provider.providerName}</span>
                      </SsoProviderMenuItem>
                    </MenuItem>
                  ))}
                </Select>
              )}
            </SsoProviderContent>
          </SsoProviderSection>

          {/* 다음 버튼 */}
          <NextButton
            variant="contained"
            size="2extraLarge"
            fullWidth
            disabled={isNextDisabled}
            sx={{ background: `${theme.colors['bg-gradient']}` }}
            onClick={handleNextClick}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Typography component="span" variant="button2" color={theme.colors['text-white']}>
                {t('Common.next')}
              </Typography>
            )}
          </NextButton>
        </FormWrapper>
      </Container>
    </PageTemplate>
  );
}, { allowLimitedToken: true });

const Container = styled.div<{ $isItAdminPage?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 130px 0px 0px 0px;
  min-height: calc(100vh - 130px - var(--employee-gnb-height));
  height: 100%;
  background-color: ${({ theme }) => theme.colors['bg-white']};
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 380px;
`;

const TitleWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const SelectWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-bottom: 14px;
`;

const SsoProviderSection = styled.div<{ $visible: boolean }>`
  width: 100%;
  overflow: hidden;
  max-height: ${({ $visible }) => ($visible ? '200px' : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '-8px')});
  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: ${({ $visible }) => ($visible ? '14px' : '0')};
`;

const SsoProviderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SsoProviderMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NextButton = styled(Button)`
  box-shadow: none;
  border: none;
  padding: 15px 24px;
  &:hover {
    box-shadow: none;
    border: none;
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
  }
`;
